import { useEffect, useState } from 'react';

import { IconLink } from '@tabler/icons-react';

import { createFileRoute, useRouter, useSearch } from '@tanstack/react-router';

import { Alert, Button, Container, Group, Stack, Text, Title } from '@optiro/ui';

import { useConfirmSeatAdd } from '@/hooks/useConfirmSeatAdd';
import { useLinkPartner } from '@/hooks/usePartners/usePartners';

export const Route = createFileRoute('/app/partners/link')({
  component: LinkPartnerPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
    force: search.force === 'true',
  }),
});

function LinkPartnerPage() {
  const { token, force: initialForce } = useSearch({ from: '/app/partners/link' });
  const router = useRouter();
  const mutation = useLinkPartner();
  const { confirm: confirmSeatAdd } = useConfirmSeatAdd();
  const [domainMismatch, setDomainMismatch] = useState(false);

  useEffect(() => {
    if (mutation.isSuccess) {
      router.navigate({ to: '/app/partners' });
    }
  }, [mutation.isSuccess, router]);

  useEffect(() => {
    const err = mutation.error as { response?: { data?: { code?: string } } } | null;
    if (err?.response?.data?.code === 'domain_mismatch') {
      setDomainMismatch(true);
    }
  }, [mutation.error]);

  const handleLink = async (force = false) => {
    const ok = await confirmSeatAdd('partner');
    if (!ok) return;
    mutation.mutate({ token, force: force || initialForce || undefined });
  };

  return (
    <Container size="sm" py="xl">
      <Stack gap="md" maw={480} mx="auto">
        <Title order={2} ta="center">
          Link virksomhed til invitation
        </Title>
        <Text ta="center" c="dimmed" size="sm">
          Bekræft at du vil linke din virksomhed til denne partnerinvitation.
        </Text>

        {domainMismatch && (
          <Alert color="yellow" title="Email-domæne matcher ikke">
            Invitationen er sendt til et andet domæne end din konto. Vil du linke alligevel?
            <Group mt="sm" gap="sm">
              <Button
                size="xs"
                color="yellow"
                onClick={() => handleLink(true)}
                loading={mutation.isPending}
              >
                Ja, link alligevel
              </Button>
              <Button size="xs" variant="subtle" onClick={() => router.history.back()}>
                Annuller
              </Button>
            </Group>
          </Alert>
        )}

        {!domainMismatch && (
          <Button
            size="lg"
            fullWidth
            leftSection={<IconLink size={18} />}
            loading={mutation.isPending}
            onClick={() => handleLink()}
          >
            Accepter og link virksomhed
          </Button>
        )}

        {mutation.isError && !domainMismatch && (
          <Alert color="red">
            {(mutation.error as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ?? 'Noget gik galt'}
          </Alert>
        )}
      </Stack>
    </Container>
  );
}
